/**
 * トップの「打ち上げ予定」ブロック（直答 + 一覧表 + Event JSON-LD）の共有レンダラ。
 * =====================================================================
 * ★なぜ切り出したか（2026-08-04 / GEO対策）
 *
 *   従来この組み立ては scripts/build-site-pages.ts の中だけにあり、
 *   **ビルド時の同梱スナップショットで焼いたまま**だった。
 *   ブラウザ利用者は JS が /api/launches（KV）から取り直すので最新を見るが、
 *   **JavaScript を実行しないクローラ（GPTBot / ClaudeBot / PerplexityBot /
 *   OAI-SearchBot など、AI検索の取得側はほぼ全部これ）は、最後に
 *   `npm run build:fallback` を叩いた時点の表しか読めない**。
 *   実測では本番の素HTMLが4日前で凍っていた（2026-07-31 09:25 時点 / 実測日 08-04）。
 *
 *   「次の打ち上げはいつ？」に答えるサイトが、AIから見ると常に数日〜数十日
 *   古い答えを返している状態は、GEO（AI検索での引用）では致命的に効く。
 *   → functions/_middleware.ts が配信時に KV の実データでこのブロックを差し替える。
 *     その差し替え後の中身と、ビルド時に焼く中身を**同じ関数から出す**ためのモジュール。
 *
 * ★クローキングにしない
 *   素のHTML・レンダ後DOM・配信時差し替えの3者が、同じデータなら同じ文字列になる。
 *   差があるのは「いつ時点のデータか」だけで、それは本文に明記される。
 *
 * ★Event JSON-LD をここに含める理由
 *   従来 Event 構造化データは src/app.ts が**実行時に document.head へ注入**していた。
 *   Googlebot はレンダリングするので拾えるが、AIクローラは JS を実行しないため
 *   本番の素HTMLには Event が1件も無かった（2026-08-04 実測: `"Event"` 出現 0回）。
 *   JSON-LD は body 内でも有効なので、このブロックに同梱して素HTMLへ載せる。
 */
import { LAUNCH_SITES } from '../data/launch-sites';
import { missionJa } from '../data/mission-names-ja';
import {
  renderAnswerHtml,
  renderScheduleTableHtml,
  type LaunchSummaryItem,
} from './launch-summary';
import { buildLaunchEventsJsonLd, type LaunchLike } from './seo';
import { precisionInfo } from './format';

/** 差し替え位置の目印。public/index.html にこの2つが無いとビルドが失敗する。 */
export const SENTINEL_START = '<!-- LAUNCH-SUMMARY:START -->';
export const SENTINEL_END = '<!-- LAUNCH-SUMMARY:END -->';

/** 「種子島宇宙センター（吉信射点 LA-Y）」→「種子島宇宙センター」 */
export function shortName(nameJa: string): string {
  return nameJa.replace(/（.*/, '');
}

/** 射場ラベル解決（一覧表の「射場」列）。 */
export const siteLabelResolver = (id: string | null): { short: string } | null => {
  const s = LAUNCH_SITES.find((x) => x.id === id);
  return s ? { short: shortName(s.nameJa) } : null;
};

/** ミッション和名の解決。台帳に無ければ null（英語名のまま出す）。 */
export const jaMission = (l: LaunchSummaryItem): string | null =>
  missionJa(
    l.mission ?? l.name.split('|')[1]?.trim() ?? null,
    l.rocket ?? l.name.split('|')[0]?.trim() ?? null,
  );

/** 射場の座標・住所（Event の location 用）。無ければ null＝そのEventは出さない。 */
const siteGeoResolver = (id: string | null) => {
  const s = LAUNCH_SITES.find((x) => x.id === id);
  return s
    ? {
        nameJa: s.nameJa,
        lat: s.lat,
        lon: s.lon,
        addressRegion: s.addressRegion,
        addressLocality: s.addressLocality,
      }
    : null;
};

/**
 * JSON-LD を <script> に埋める際のエスケープ。
 * `</script>` が JSON 文字列の中に現れると、そこで script 要素が閉じてしまう
 * （ミッション名は外部APIとローカル台帳由来で、将来 `<` を含みうる）。
 * HTMLエスケープは使えない（JSON-LDの中身が壊れる）ので、`<` を < へ逃がす。
 */
function jsonLdSafe(value: unknown): string {
  return JSON.stringify(value).replace(/</g, '\\u003c');
}

/** このブロックが載るページ（トップ）の正規URL。dateModified の主語になる。 */
const PAGE_URL = 'https://uchiagebiyori.com/';

/**
 * 打ち上げデータ由来の JSON-LD（<script>1本）。中身は2種類：
 *
 *  1. WebPage の `dateModified` … このページの打ち上げ情報が「いつ時点か」を機械可読で宣言する。
 *     AI検索は同じ問いに答える複数のページから1つを選ぶとき鮮度を見るので、
 *     本文の「◯◯時点」と同じ事実を構造化データにも出しておく。
 *     ⚠️ 値は capturedAt（LL2から取った時刻）であって配信時刻ではない。
 *        アクセスのたびに今の時刻を書くと「毎秒更新されている」という嘘になる。
 *  2. 確定日付の打ち上げの schema.org/Event（日付が粗い回は出さない＝捏造しない）。
 *
 * @returns 0件でも WebPage ノードは出るので、常に非空の <script> を返す。
 */
export function buildLaunchJsonLd(
  launches: LaunchSummaryItem[],
  capturedAt: string,
  nowMs: number,
): object[] {
  const events = buildLaunchEventsJsonLd(
    launches as unknown as LaunchLike[],
    siteGeoResolver,
    (p) => precisionInfo(p).bookable,
    (p) => precisionInfo(p).tier === 'time',
    nowMs,
  );
  const page = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    '@id': PAGE_URL,
    url: PAGE_URL,
    name: '国内ロケット打ち上げ予定カレンダー｜打ち上げ日和',
    inLanguage: 'ja',
    ...(Number.isFinite(Date.parse(capturedAt)) ? { dateModified: capturedAt } : {}),
    isBasedOn: 'https://thespacedevs.com/llapi',
  };
  return [page, ...events];
}

/** JSON-LD の <script> 要素に付ける id。静的HTMLとJS側で同じものを使い、二重に出さない。 */
export const LD_SCRIPT_ID = 'ld-events';

/** buildLaunchJsonLd() の結果を <script> にしたもの（素HTMLへ焼く用）。 */
export function renderLaunchJsonLd(
  launches: LaunchSummaryItem[],
  capturedAt: string,
  nowMs: number,
): string {
  const payload = buildLaunchJsonLd(launches, capturedAt, nowMs);
  return `<script type="application/ld+json" id="${LD_SCRIPT_ID}">${jsonLdSafe(payload)}</script>`;
}

/**
 * トップの打ち上げブロック全体（直答 + 一覧表 + Event JSON-LD）。
 *
 * @param launches   表示対象の打ち上げ（過ぎた回は呼び出し側で pruneStaleLaunches 済みであること）
 * @param capturedAt このデータをLL2から取得した時刻（ISO）。本文の「◯◯時点」に出る
 * @param nowMs      Event の地平線ゲートの基準時刻
 */
export function renderLaunchBlock(
  launches: LaunchSummaryItem[],
  capturedAt: string,
  nowMs: number,
): string {
  return (
    renderAnswerHtml(launches, capturedAt, siteLabelResolver, jaMission) +
    renderScheduleTableHtml(launches, capturedAt, siteLabelResolver, { jaMission }) +
    renderLaunchJsonLd(launches, capturedAt, nowMs)
  );
}

/**
 * HTML のセンチネル間を block で置換する。
 * センチネルが無い / 順序が逆なら null を返す（＝呼び出し側は差し替えを諦めて素のまま返す）。
 * 配信時に使うので、想定外の入力で例外を投げてページを落とさないこと。
 */
export function replaceLaunchBlock(html: string, block: string): string | null {
  const i = html.indexOf(SENTINEL_START);
  const j = html.indexOf(SENTINEL_END);
  if (i < 0 || j < 0 || j < i) return null;
  return html.slice(0, i + SENTINEL_START.length) + '\n' + block + '\n      ' + html.slice(j);
}
